import { useSyncExternalStore } from 'react';
import type {
  DesignBrief,
  DesignDirective,
  DesignSpec,
  FormatId,
  ImageAnalysis,
  StyleId,
} from '@/types';
import { deepClone } from '@/lib/utils';

/**
 * A tiny observable store with an undo/redo history.
 *
 * Deliberately hand-rolled rather than pulled from a library: the only
 * non-trivial requirement is that history snapshots capture the *design*
 * without capturing transient UI state (which panel is open, whether a drag is
 * in flight), and that is easier to express directly than to configure.
 */

export interface AppState {
  // Inputs
  photoDataUrl: string | null;
  photoName: string | null;
  logoDataUrl: string | null;
  title: string;
  subtitle: string;
  kicker: string;
  styleId: StyleId;
  format: FormatId;

  // Engine output
  analysis: ImageAnalysis | null;
  directive: DesignDirective | null;
  variants: DesignSpec[];
  activeId: string | null;

  // UI
  status: 'idle' | 'analyzing' | 'generating' | 'ready' | 'error';
  statusMessage: string;
  usedProvider: string | null;
  selectedBlockId: string | null;
  showGuides: boolean;
  showFaces: boolean;
  preferAI: boolean;
  aiConfigured: boolean;
}

const initialState: AppState = {
  photoDataUrl: null,
  photoName: null,
  logoDataUrl: null,
  title: 'Natural Results Through Modern Rhinoplasty',
  subtitle: 'Consultation-led surgical planning, tailored to your anatomy.',
  kicker: 'Aesthetic Surgery',
  styleId: 'medical',
  format: 'ig-portrait',
  analysis: null,
  directive: null,
  variants: [],
  activeId: null,
  status: 'idle',
  statusMessage: 'Upload a photo, or start from a sample scene.',
  usedProvider: null,
  selectedBlockId: null,
  showGuides: false,
  showFaces: false,
  preferAI: true,
  aiConfigured: false,
};

/** Only these fields participate in undo/redo. */
interface HistorySnapshot {
  variants: DesignSpec[];
  activeId: string | null;
  title: string;
  subtitle: string;
  kicker: string;
  styleId: StyleId;
  format: FormatId;
}

const MAX_HISTORY = 60;

class DesignStore {
  private state: AppState = initialState;
  private listeners = new Set<() => void>();
  private past: HistorySnapshot[] = [];
  private future: HistorySnapshot[] = [];

  getState = (): AppState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private emit(): void {
    for (const listener of this.listeners) listener();
  }

  private snapshot(): HistorySnapshot {
    return {
      variants: deepClone(this.state.variants),
      activeId: this.state.activeId,
      title: this.state.title,
      subtitle: this.state.subtitle,
      kicker: this.state.kicker,
      styleId: this.state.styleId,
      format: this.state.format,
    };
  }

  /** Update without touching history — for transient/UI-only changes. */
  set(patch: Partial<AppState>): void {
    this.state = { ...this.state, ...patch };
    this.emit();
  }

  /** Update and push the previous design onto the undo stack. */
  commit(patch: Partial<AppState>, _label = 'edit'): void {
    this.past.push(this.snapshot());
    if (this.past.length > MAX_HISTORY) this.past.shift();
    this.future = [];
    this.state = { ...this.state, ...patch };
    this.emit();
  }

  /**
   * Coalesce a stream of changes (a slider drag) into one history entry: the
   * first call records history, subsequent calls with the same key do not.
   */
  private lastCoalesceKey: string | null = null;
  private coalesceTimer: number | null = null;

  coalesced(key: string, patch: Partial<AppState>): void {
    if (this.lastCoalesceKey !== key) {
      this.past.push(this.snapshot());
      if (this.past.length > MAX_HISTORY) this.past.shift();
      this.future = [];
      this.lastCoalesceKey = key;
    }
    if (this.coalesceTimer !== null) window.clearTimeout(this.coalesceTimer);
    this.coalesceTimer = window.setTimeout(() => {
      this.lastCoalesceKey = null;
    }, 700);

    this.state = { ...this.state, ...patch };
    this.emit();
  }

  undo(): void {
    const previous = this.past.pop();
    if (!previous) return;
    this.future.push(this.snapshot());
    this.state = { ...this.state, ...previous };
    this.lastCoalesceKey = null;
    this.emit();
  }

  redo(): void {
    const next = this.future.pop();
    if (!next) return;
    this.past.push(this.snapshot());
    this.state = { ...this.state, ...next };
    this.lastCoalesceKey = null;
    this.emit();
  }

  canUndo(): boolean {
    return this.past.length > 0;
  }

  canRedo(): boolean {
    return this.future.length > 0;
  }

  clearHistory(): void {
    this.past = [];
    this.future = [];
    this.lastCoalesceKey = null;
    this.emit();
  }

  // --- Design helpers ----------------------------------------------------

  getActive(): DesignSpec | null {
    return this.state.variants.find((v) => v.id === this.state.activeId) ?? null;
  }

  /** Replace the active design, recording one history entry. */
  updateActive(
    updater: (spec: DesignSpec) => DesignSpec,
    coalesceKey?: string,
  ): void {
    const active = this.getActive();
    if (!active) return;
    const next = updater(deepClone(active));
    const variants = this.state.variants.map((v) => (v.id === next.id ? next : v));
    if (coalesceKey) this.coalesced(coalesceKey, { variants });
    else this.commit({ variants });
  }
}

export const store = new DesignStore();

export function useStore<T>(selector: (state: AppState) => T): T {
  return useSyncExternalStore(
    store.subscribe,
    () => selector(store.getState()),
    () => selector(initialState),
  );
}

export function useHistoryFlags(): { canUndo: boolean; canRedo: boolean } {
  return useSyncExternalStore(
    store.subscribe,
    () => {
      const flags = `${store.canUndo()}:${store.canRedo()}`;
      if (flags !== cachedFlagKey) {
        cachedFlagKey = flags;
        cachedFlags = { canUndo: store.canUndo(), canRedo: store.canRedo() };
      }
      return cachedFlags;
    },
    () => ({ canUndo: false, canRedo: false }),
  );
}

// useSyncExternalStore requires a referentially stable snapshot, so the derived
// flags object is memoized on its own value.
let cachedFlagKey = 'false:false';
let cachedFlags = { canUndo: false, canRedo: false };

export function currentBrief(state: AppState): DesignBrief {
  return {
    title: state.title,
    subtitle: state.subtitle,
    kicker: state.kicker,
    styleId: state.styleId,
    format: state.format,
    hasLogo: Boolean(state.logoDataUrl),
  };
}
